import { formatDate, NgStyle, NgClass } from '@angular/common';
import { AfterContentInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DisplayGrid, GridsterConfig, GridType, GridsterComponent, GridsterItemComponent } from 'angular-gridster2';
import { filter, find, forEach, range } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import { finalize } from 'rxjs/internal/operators/finalize';
import { SpinnerService } from 'src/app/shared/components/spinner/spinner.service';
import { AlertsService } from 'src/app/shared/services/alerts.service';
import { MenuDisplay, MenuDisplayConfig } from '../../interfaces/menu-display';
import { MenuWidget } from '../../interfaces/menu-widget';
import { MenuDisplayService } from '../../services/menu-display.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { filter as rxJsFilter } from 'rxjs/operators';
import { MenuDisplaySetting } from "../../../configurator/terminals/interface/menu-display-setting";
import { TerminalsService } from "../../../configurator/terminals/services/terminals.service";
import { DomainConstants } from "../../../shared/constants/domain-constants";
import { ApplicationStateService } from "../../../shared/services/application-state.service";
import { RabbitMQService } from "../../../shared/services/rabbitmq/services/rabbitmq.service";
import { TerminalStatusCheckService } from "../../../shared/services/terminal-status-check.service";
import { MenuWidgetService } from "../../services/menu-widget.service";
import { WidgetDetails } from "../../interfaces/widget-details";
import { MenuWidgetComponent } from '../menu-widget/menu-widget.component';
import { SlideShowData } from 'src/app/shared/interface/slide-show-data';
import { MediaRotatorComponent } from 'src/app/shared/components/media-rotator/media-rotator.component';
import { SlideShowDataService } from 'src/app/shared/services/slide-show-data.service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'pos-menu-display-preview',
    templateUrl: './menu-display-preview.component.html',
    styleUrls: ['./menu-display-preview.component.scss'],
    imports: [NgStyle, GridsterComponent, GridsterItemComponent, NgClass, MenuWidgetComponent, MediaRotatorComponent]
})
export class MenuDisplayPreviewComponent implements OnInit, AfterContentInit, OnDestroy {

  menuId: number;
  options: GridsterConfig;
  menuWidgets: Array<MenuWidget> = [];
  digitalMenu: MenuDisplay;
  menuDisplayConfig: MenuDisplayConfig;

  menuDisplayOverrides: Array<MenuDisplaySetting> = [];
  lastLoadedMenuId = 0;
  delay = null;
  terminalType = '';

  autoRefreshWidgetInterval: any;
  rabbitMqFilterMenuItemsSubscription: Subscription;
  rabbitMqHighlightMenuItemsSubscription: Subscription;
  rabbitMqRefreshMenuItemsSubscription: Subscription;
  userClockOutSubscription: Subscription;
  refreshTerminalRequestSubscription: Subscription;
  rabbitMQTerminalStatusCheckSubscription: Subscription;
  overrideMenuId: number;
  groupWidgetName = DomainConstants.DigitalMenuWidgetTypes.Group.Name;
  isAdHocMenuDisplayed = false;
  slideshowData: any = null;
  playSlideShowOnMenuScreensSubscription: Subscription;
  overlayHideTimeout: any = null;

  constructor(private route: ActivatedRoute,
    private spinnerService: SpinnerService,
    private alertService: AlertsService,
    private router: Router,
    private applicationStateService: ApplicationStateService,
    protected terminalService: TerminalsService,
    private menuDisplayService: MenuDisplayService,
    protected rabbitMQService: RabbitMQService,
    private terminalStatusChecker: TerminalStatusCheckService,
    private slideShowDataService: SlideShowDataService,
    public menuWidgetService: MenuWidgetService) {
    this.menuId = route.snapshot.params.id ? parseInt(route.snapshot.params.id, 10) : 0;
    this.terminalType = route?.snapshot?.data?.type ? route.snapshot.data.type : applicationStateService.terminalType;
  }

  ngAfterContentInit(): void {
    this.subscribeToFilterMenuItemsBroadcastQueue();
    this.subscribeToHighlightMenuItemsBroadcastQueue();
    this.subscribeToRefreshMenuDisplayBroadcastQueue();
    this.subscribeToTerminalLogoutMessage();
    this.subscribeTerminalRefreshMessage();
    this.subscribeToTerminalStatusCheck();
    this.subscribeToSwitchMenuDisplayMessage();
    this.subscribeToPlaySlideShowOnMenuScreensMessage();
  }

  ngOnInit(): void {
    this.setGridConfigurations();
    if (this.terminalType === DomainConstants.TerminalTypes.MENU_DISPLAY.Name) {
      this.loadMenuDisplaySettings();
    } else {
      this.loadData(this.menuId, false, true);
    }
  }

  ngOnDestroy(): void {
    if (this.autoRefreshWidgetInterval) {
      clearInterval(this.autoRefreshWidgetInterval);
    }
    if (this.overlayHideTimeout) {
      clearTimeout(this.overlayHideTimeout);
    }
  }

  private setGridConfigurations() {
    this.options = {
      gridType: GridType.Fixed,
      displayGrid: DisplayGrid.None,
      pushItems: true,
      draggable: {
        enabled: false
      },
      resizable: {
        enabled: false
      },
      margin: 0,
      maxItemCols: 1000000,
      maxItemRows: 1000000,
      maxItemArea: 1000000000,
      fixedColWidth: 5,
      fixedRowHeight: 5,
      maxRows: 2000,
      maxCols: 2000,
      allowMultiLayer: true,
      defaultLayerIndex: 1,
      maxLayerIndex: 2,
      baseLayerIndex: 1,
    };
  }

  loadMenuDisplaySettings() {
    this.spinnerService.show();
    this.terminalService.getMenuDisplaySettings(this.applicationStateService.terminalId)
      .pipe(finalize(() => {
        this.spinnerService.hide();
      }))
      .subscribe({
        next: (menuDisplaySettings) => {
          this.menuDisplayOverrides = menuDisplaySettings;
          this.setOverriddenMenu();
        }, error: this.alertService.showApiError
      });
  }

  setOverriddenMenu() {
    const today = new Date();
    const currentTime = formatDate(today, 'HH:mm:ss', 'en');
    this.overrideMenuId = null;
    if (this.menuDisplayOverrides.length) {
      const currentOverriddenMenu: MenuDisplaySetting = find(this.menuDisplayOverrides, (x) => { 
        let parsedEndTime = this.parseTime(x.EndTime);
        return this.parseTime(x.StartTime) <= currentTime
        && (currentTime) < parsedEndTime && x.Days.includes(today.getDay().toString())
    });
      if (currentOverriddenMenu) {
        this.isAdHocMenuDisplayed = false;
        this.overrideMenuId = currentOverriddenMenu.MenuDisplayId;
        const endDateTime =  this.convertStringToTime(currentOverriddenMenu.EndTime).getTime()
        this.delay = endDateTime - today.getTime();
        this.setMenuOnTimeout();
        this.loadData(this.overrideMenuId);
      } else {
        this.checkForNextOverriddenMenu();
      }
    } else {
      this.loadData(this.menuId);
    }
  }

  checkForNextOverriddenMenu() {
    const today = new Date();
    const currentTime = formatDate(today, 'HH:mm:ss', 'en');
    const nextOverriddenMenusOfDay: Array<MenuDisplaySetting> = filter(this.menuDisplayOverrides,
      x => x.Days.includes(today.getDay().toString()) &&
        currentTime <= this.parseTime(x.StartTime)).sort((a, b) => a.StartTime.localeCompare(b.StartTime));
    if (nextOverriddenMenusOfDay.length) {
      this.delay = this.convertStringToTime(nextOverriddenMenusOfDay[0].StartTime).getTime() - today.getTime();
    } else {
      this.getNextDayTimeout();
    }
    !this.isAdHocMenuDisplayed && this.loadData(this.menuId);
    this.setMenuOnTimeout();
  }

  getNextDayTimeout() {
    const today = new Date();
    const daysOfWeek = range(0, 7);
    const days = [...daysOfWeek.slice(today.getDay() + 1, daysOfWeek.length),
    ...daysOfWeek.slice(0, today.getDay() + 1)];
    forEach(days, (day, i) => {
      const overriddenMenus = filter(this.menuDisplayOverrides, x => x.Days.includes(day.toString()))
        .sort((a, b) => a.StartTime.localeCompare(b.StartTime));
      if (overriddenMenus.length) {        
        this.delay = this.nextDate(i + 1, overriddenMenus[0].StartTime).getTime() - today.getTime();
        return false;
      }
    });
  }

  nextDate(x, time: string) {
    const hoursAndMinutes = time.split(':').map(t => parseInt(t, 10));
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + x);
    nextDate.setHours(hoursAndMinutes[0], hoursAndMinutes[1], hoursAndMinutes[2]);
    return nextDate;
  }

  setMenuOnTimeout() {
    const menuOverrideTimeout = setTimeout(() => {
      this.setOverriddenMenu();
      if (menuOverrideTimeout) {
        clearTimeout(menuOverrideTimeout);
      }
    }, this.delay);
  }

  convertStringToTime(time: string) {
    const hoursAndMinutes = time.split(':').map(x => parseInt(x, 10));
    const date = new Date();
    date.setHours(hoursAndMinutes[0], hoursAndMinutes[1], hoursAndMinutes[2]);
    return date;
  }

  loadData(menuId: number, autoRefresh: boolean = false, isSilentReload: boolean = true) {
    if (menuId !== this.lastLoadedMenuId || autoRefresh) {
      const observable: Array<Observable<any>> = [];
      observable.push(this.menuDisplayService.getMenuWidgets(menuId));
      observable.push(this.menuDisplayService.get(menuId));
      observable.push(this.menuWidgetService.getMenuWidgets(menuId));
      if (!isSilentReload) {
        this.spinnerService.show();
      }
      forkJoin(observable).pipe(finalize(() => {
          this.spinnerService.hide();
      })).subscribe({
        next: ([menuWidgets, digitalMenu, widgets]: [Array<MenuWidget>, MenuDisplay, Array<WidgetDetails>]) => {
          this.lastLoadedMenuId = menuId;
          this.menuWidgets = menuWidgets;
          this.menuWidgets.forEach((data) => {
            data.GridItem = JSON.parse(data.WidgetDetails);
          });
          this.digitalMenu = digitalMenu;
          this.menuDisplayConfig = JSON.parse(this.digitalMenu.Config);
          this.options.maxCols = ((digitalMenu.Orientation ? digitalMenu.Height : digitalMenu.Width) / this.options.fixedColWidth) * 2;
          this.options.maxRows = ((digitalMenu.Orientation ? digitalMenu.Width : digitalMenu.Height) / this.options.fixedRowHeight) * 2;
          this.options.maxItemArea = (this.options.maxCols * this.options.maxRows);
          this.menuWidgetService.widgetDataSubject.next(widgets);
          this.autoRefreshMenuDisplay();
        },
        error: this.alertService.showApiError
      });
    }
  }

  autoRefreshMenuDisplay() {
    if (this.autoRefreshWidgetInterval) {
      clearInterval(this.autoRefreshWidgetInterval);
    }
    const menuDisplayAutoRefreshIntervalMinutes = this.applicationStateService.settingParam?.MenuDisplayAutoRefreshIntervalMinutes;
    this.autoRefreshWidgetInterval = setInterval(() => {
      this.loadWidget();
    }, 60 * 1000 * (menuDisplayAutoRefreshIntervalMinutes ? menuDisplayAutoRefreshIntervalMinutes : 10));
  }

  loadWidget() {
    this.menuWidgetService.getMenuWidgets(this.overrideMenuId??this.menuId)
    .subscribe({
      next: (res: Array<WidgetDetails>) => {
        this.menuWidgetService.widgetDataSubject.next(res);
        this.autoRefreshMenuDisplay();
      },
      error: this.alertService.showApiError
    });
  }

  subscribeToFilterMenuItemsBroadcastQueue() {
    this.rabbitMqFilterMenuItemsSubscription = this.rabbitMQService.subscribeToMenuDisplayFilteredMessage$()
      .subscribe({
        next: (message: any) => {
          this.menuWidgetService.menuItemChangeSubject.next({ Type: 'FilterMenuItems', Message: message })
        }, error: this.alertService.showApiError
      });
  }

  subscribeToHighlightMenuItemsBroadcastQueue() {
    this.rabbitMqHighlightMenuItemsSubscription = this.rabbitMQService.subscribeToMenuDisplayItemHighlightedMessage$()
      .subscribe({
        next: (message: any) => {
          this.menuWidgetService.menuItemChangeSubject.next({ Type: 'HighlightMenuItems', Message: message })
        }, error: this.alertService.showApiError
      });
  }

  subscribeToTerminalLogoutMessage() {
    this.userClockOutSubscription = this.rabbitMQService.subscribeToUserClockOutMessage$()
      .subscribe({
        next: (message: any) => {
          if (message.Payload.UserId === this.applicationStateService.userId) {
            this.router.navigate(['login']);
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          }
        }, error: () => {
          console.log('error while connecting to RabbitMQ.');
        }
      });
  }

  subscribeTerminalRefreshMessage() {
    this.refreshTerminalRequestSubscription = this.rabbitMQService.subscribeToRefreshTerminal$(this.applicationStateService.terminalId)
    .subscribe({
      next: () => {
        window.location.reload();
      }
    })
  }

  subscribeToRefreshMenuDisplayBroadcastQueue() {
    this.rabbitMqRefreshMenuItemsSubscription = this.rabbitMQService.subscribeToRefreshMenuDisplayMessage$()
      .pipe(rxJsFilter(message => message.Payload.MenuDisplayId == (this.overrideMenuId ?? this.menuId)))
      .subscribe({
        next: (message) => {
          this.loadData(this.overrideMenuId ?? this.menuId, true);
        }, error: this.alertService.showApiError
      });
  }

  subscribeToTerminalStatusCheck() {
    this.rabbitMQTerminalStatusCheckSubscription = this.terminalStatusChecker.subscribeTerminalStatusCheck(this.applicationStateService.terminalId)
    .subscribe({
      next: (res) => this.terminalStatusChecker.publishTerminalStatusResponse(res)
    })
  }

  subscribeToSwitchMenuDisplayMessage() {
    this.rabbitMQTerminalStatusCheckSubscription = this.rabbitMQService.subscribeToSwitchMenuDisplayMessage$(this.applicationStateService.terminalId)
    .subscribe({
      next: (message) => {
        if (message.Payload.TerminalId === this.applicationStateService.terminalId) {
          this.isAdHocMenuDisplayed = true;
          this.loadData(message.Payload.MenuId);
        }
      }, error: this.alertService.showApiError
    })
  }

  subscribeToPlaySlideShowOnMenuScreensMessage() {
    this.playSlideShowOnMenuScreensSubscription = this.rabbitMQService.subscribeToPlaySlideShowOnMenuScreensMessage$(this.applicationStateService.terminalId)
    .subscribe({
      next: (message) => {
        if (message.Payload.TerminalId === this.applicationStateService.terminalId) {
          this.hideSlideshow();
          const timeout = setTimeout(() => {
            this.setSlideshow(message.Payload.Slideshow, message.Payload.Duration);
            clearTimeout(timeout);
          });
        }
      }, error: this.alertService.showApiError
    })
  }

  getSlideshowCycleDuration(): number {
    return this.slideshowData.reduce((total, slide) => total + (slide.duration || 2000), 0);
  }

  setSlideshow(slideShowData: Array<SlideShowData>, payloadDuration?: number) {
    this.slideshowData = this.slideShowDataService.mapSlideShowDetails(slideShowData);
    this.slideshowData = this.slideShowDataService.filterActiveSlides(this.slideshowData);
    const hideAfterMs = payloadDuration > 0
      ? payloadDuration * 1000
      : this.getSlideshowCycleDuration();
    if (this.slideshowData?.length > 0 && hideAfterMs > 0) {
      this.overlayHideTimeout = setTimeout(() => {
        this.hideSlideshow();
      }, hideAfterMs);
    }
  }

  hideSlideshow() {
    this.slideshowData = null;
    if (this.overlayHideTimeout) {
      clearTimeout(this.overlayHideTimeout);
      this.overlayHideTimeout = null;
    }
  }

  parseTime(timeString) {
    const time = timeString.split(':');
    const date = new Date();
    date.setHours(time[0], time[1], time[2]);
    return formatDate(date, 'HH:mm:ss', 'en');
  }

}
